import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { artifactService, prototyperService } from '@ideoz/core';
import type { PublishedKind } from './published.types';

interface PublishedState {
  isLoading: boolean;
  error: string | null;
  demoUrl: string | null;
  title: string;
  onRetry: () => void;
}

const CONFIG: Record<
  PublishedKind,
  {
    prefix: RegExp;
    defaultTitle: string;
    fetch: (slug: string) => Promise<{ demoUrl?: string | null; header?: string | null }>;
  }
> = {
  prototype: {
    prefix: /^\/p\//,
    defaultTitle: 'Published Prototype',
    fetch: (slug) => prototyperService.getPrototyperBySlug(slug),
  },
  artifact: {
    prefix: /^\/a\//,
    defaultTitle: 'Published Artifact',
    fetch: (slug) => artifactService.getArtifactBySlug(encodeURIComponent(slug)),
  },
};

/**
 * Fetch-by-slug logic shared by the public `/p/*` (prototype) and `/a/*`
 * (artifact) pages: reads the slug from the URL, loads the resource, and sets
 * the document title. Owner-agnostic beyond the {@link PublishedKind} config.
 */
export function usePublishedResource(kind: PublishedKind): PublishedState {
  const location = useLocation();
  const config = CONFIG[kind];
  const slug = location.pathname.replace(config.prefix, '').replace(/^\//, '');

  const [demoUrl, setDemoUrl] = useState<string | null>(null);
  const [title, setTitle] = useState<string>(config.defaultTitle);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!slug) {
        setError('No slug provided');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setError(null);
        const data = await config.fetch(slug);
        if (cancelled) return;
        setDemoUrl(data.demoUrl ?? null);
        const pageTitle = data.header?.trim() || config.defaultTitle;
        setTitle(pageTitle);
        document.title = pageTitle;
      } catch (err) {
        if (cancelled) return;
        console.error(`Error fetching ${kind}:`, err);
        setError(err instanceof Error ? err.message : `Failed to load ${kind}`);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const onRetry = () => {
    setIsLoading(true);
    setError(null);
    window.location.reload();
  };

  return { isLoading, error, demoUrl, title, onRetry };
}
